import React, { useMemo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import styled from '@emotion/styled';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { LaunchModalMode } from '@/components/Focus/AvailsSchedule/IncrementalLaunchModal/types';
import { IncrementalSelectionState } from '@/components/Focus/AvailsSchedule/NewSchedule/ReviewSchedule/IncrementalLaunchRightsSelectionSection';
import { ScheduleAvailType, ScheduleAvailRights } from '@/redux/slices/slice_types';
import { formatDateTimePartsLocal } from '@/components/Focus/utils/newScheduleAvails';
import { useFormatAvailDisplayLabel } from '@/components/Focus/hooks/useFormatAvailDisplayLabel';

interface Props {
  mode: LaunchModalMode;
  startDate?: string;
  endDate?: string;
  rights: Record<ScheduleAvailType, IncrementalSelectionState>;
  originalRights: ScheduleAvailRights;
}

const StepTwo: React.FC<Props> = ({ mode, startDate, endDate, rights, originalRights }) => {
  const source = mode === LaunchModalMode.Add ? 'add' : 'remove';
  const langDiffCount = rights.audio[source].length + rights.timedtext[source].length;
  const territoriesDiffCount = rights.territories[source].length;
  const modeName = mode === LaunchModalMode.Add ? 'Expansion' : 'Takedown';

  const formatDisplayLabelForType = useFormatAvailDisplayLabel();

  const displayStartDate = useMemo(() => (startDate ? formatDateTimePartsLocal(startDate, undefined, 'short') : undefined), [startDate]);
  const displayEndDate = useMemo(() => (endDate ? formatDateTimePartsLocal(endDate, undefined, 'short') : undefined), [endDate]);

  return (
    <div>
      {mode === LaunchModalMode.Add && startDate && (
        <DateRow>
          <DateRowLabel>Start date</DateRowLabel>
          <DateRowValue>
            {displayStartDate?.date} {displayStartDate?.time}
          </DateRowValue>
        </DateRow>
      )}
      {mode === LaunchModalMode.Remove && (
        <DateRow>
          <DateRowLabel>End date</DateRowLabel>
          <DateRowValue>
            {endDate ? (
              <>
                {displayEndDate?.date} {displayEndDate?.time}
              </>
            ) : (
              'No end date'
            )}
          </DateRowValue>
        </DateRow>
      )}

      <Block>
        <BlockTitle variant="h3">
          Language {modeName}
          {langDiffCount > 0 ? (
            <DiffCount isNegative={mode === 'remove'}>
              {mode === 'add' ? '+' : '-'}
              {langDiffCount}
            </DiffCount>
          ) : (
            <NoChangesText>No changes</NoChangesText>
          )}
        </BlockTitle>

        {langDiffCount > 0 && (
          <>
            <AccordionStyled defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<PolarisIcon icon="down" />}>Changes</AccordionSummary>
              <AccordionDetails>
                <RightsList>
                  {rights.audio[mode].map((lang) => (
                    <RightsListItem key={lang}>
                      {mode === 'add' ? <AddIcon /> : <RemoveIcon />}
                      <RightsItemLabel>{formatDisplayLabelForType(lang, 'audio')}</RightsItemLabel>
                      <RightsItemType>Audio</RightsItemType>
                    </RightsListItem>
                  ))}
                  {rights.timedtext[mode].map((lang) => (
                    <RightsListItem key={lang}>
                      {mode === 'add' ? <AddIcon /> : <RemoveIcon />}
                      <RightsItemLabel>{formatDisplayLabelForType(lang, 'timedtext')}</RightsItemLabel>
                      <RightsItemType>Timed Text</RightsItemType>
                    </RightsListItem>
                  ))}
                </RightsList>
              </AccordionDetails>
            </AccordionStyled>

            {originalRights && (
              <AccordionStyled disableGutters>
                <AccordionSummary expandIcon={<PolarisIcon icon="down" />}>Previously Selected Languages</AccordionSummary>
                <AccordionDetails>
                  <RightsList>
                    {originalRights.audio.map((lang) => (
                      <RightsListItem key={lang}>
                        <OriginalIcon />
                        <RightsItemLabel>{formatDisplayLabelForType(lang, 'audio')}</RightsItemLabel>
                        <RightsItemType>Audio</RightsItemType>
                      </RightsListItem>
                    ))}
                    {originalRights.timedtext.map((lang) => (
                      <RightsListItem key={lang}>
                        <OriginalIcon />
                        <RightsItemLabel>{formatDisplayLabelForType(lang, 'timedtext')}</RightsItemLabel>
                        <RightsItemType>Timed Text</RightsItemType>
                      </RightsListItem>
                    ))}
                  </RightsList>
                </AccordionDetails>
              </AccordionStyled>
            )}
          </>
        )}
      </Block>
      <Block>
        <BlockTitle variant="h3">
          Territories {modeName}
          {territoriesDiffCount > 0 ? (
            <DiffCount isNegative={mode === 'remove'}>
              {mode === 'add' ? '+' : '-'}
              {territoriesDiffCount}
            </DiffCount>
          ) : (
            <NoChangesText>No changes</NoChangesText>
          )}
        </BlockTitle>

        {territoriesDiffCount > 0 && (
          <>
            <AccordionStyled defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<PolarisIcon icon="down" />}>Changes</AccordionSummary>
              <AccordionDetails>
                <RightsList>
                  {rights.territories[mode].map((territory) => (
                    <RightsListItem key={territory}>
                      {mode === 'add' ? <AddIcon /> : <RemoveIcon />}
                      <RightsItemLabel>{formatDisplayLabelForType(territory, 'territories')}</RightsItemLabel>
                    </RightsListItem>
                  ))}
                </RightsList>
              </AccordionDetails>
            </AccordionStyled>

            {originalRights && (
              <AccordionStyled disableGutters>
                <AccordionSummary expandIcon={<PolarisIcon icon="down" />}>Previously Selected Territories</AccordionSummary>
                <AccordionDetails>
                  <RightsList>
                    {originalRights.territories.map((territory) => (
                      <RightsListItem key={territory}>
                        <OriginalIcon />
                        <RightsItemLabel>{formatDisplayLabelForType(territory, 'territories')}</RightsItemLabel>
                      </RightsListItem>
                    ))}
                  </RightsList>
                </AccordionDetails>
              </AccordionStyled>
            )}
          </>
        )}
      </Block>
    </div>
  );
};

export default StepTwo;

const DateRow = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const DateRowLabel = styled.div`
  flex-basis: 16%;
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2, 3)};
  font-size: 1.286rem;
  font-weight: 600;
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`};
  background: ${({ theme }) => (theme as FocusTheme).palette.neutral[90]};
`;

const DateRowValue = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(0, 2)};
  font-size: 1rem;
  font-weight: 600;
  border-radius: ${({ theme }) => `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`};
  background: ${({ theme }) => (theme as FocusTheme).palette.neutral[70]};
`;

const Block = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => (theme as FocusTheme).palette.neutral[90]};
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: 10px;
`;

const BlockTitle = styled(Typography)`
  font-size: 1.286rem;
  font-weight: 700;
`;

const DiffCount = styled.span<{ isNegative: boolean }>`
  margin-left: ${({ theme }) => theme.spacing(3)};
  color: ${({ theme, isNegative }) => (isNegative ? theme.palette.error.main : theme.palette.primary.main)};
`;

const NoChangesText = styled.span`
  margin-left: ${({ theme }) => theme.spacing(3)};
  font-weight: 500;
  font-size: 1rem;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[30]};
`;

const AccordionStyled = styled(Accordion)`
  background-color: transparent;
  box-shadow: none;

  &.MuiAccordion-root,
  &.MuiAccordion-root.Mui-expanded {
    margin-top: ${({ theme }) => theme.spacing(1.5)};
    font-size: 1.143rem;
  }

  & .MuiAccordionSummary-root {
    flex-direction: initial;
  }

  & .MuiAccordionSummary-expandIconWrapper {
    &.Mui-expanded {
      transform: rotate(180deg);
    }
  }

  & .MuiAccordionDetails-root {
    margin-top: ${({ theme }) => theme.spacing(1.25)};
    padding: 0;
  }
`;

const RightsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const RightsListItem = styled.li`
  :not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing(1)};
  }

  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1, 3)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: 1px solid ${({ theme }) => theme.palette.primary.main}80;
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[80]};
`;

const RightsItemLabel = styled.div`
  flex-grow: 1;
`;

const RightsItemType = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[30]};
`;

const AddRemoveIconBase = styled.div`
  &::before {
    display: block;
  }

  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20%;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.05);
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[80]};
`;

const AddIcon = styled(AddRemoveIconBase)`
  &::before {
    content: '+';
  }
  background-color: ${({ theme }) => theme.palette.primary.main};
`;

const RemoveIcon = styled(AddRemoveIconBase)`
  &::before {
    content: '-';
    position: relative;
    top: -1px;
  }

  background-color: ${({ theme }) => theme.palette.error.main};
`;

const OriginalIcon = styled(AddRemoveIconBase)`
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[40]};
`;
